import { useState, useEffect } from 'react';
import { getRadarData, createCallSession } from '../api/radar';
import { errorCode, timer, track } from '../../analytics';

export const useCallSession = (awb, shouldCreateSession) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [loadingStage, setLoadingStage] = useState('radar'); // 'radar' or 'session'

  const initiateCallSession = async () => {
    if (!shouldCreateSession || !awb) return;

    setIsLoading(true);
    setError(null);
    setPhoneNumber(null);
    setLoadingStage('radar');
    track('tp_call_session', { step: 'requested', via: 'deep_link' });
    const elapsed = timer();
    let errorClass = 'service';
    let errorResponse;

    try {
      // Step 1: Get radar data to extract tripId
      const radarResponse = await getRadarData(awb);
      
      if (!radarResponse) {
        errorClass = 'network';
        throw new Error('Failed to fetch trip details. Please check your connection.');
      }

      if (!radarResponse.data || !radarResponse.data.tripId) {
        errorClass = 'no_trip';
        errorResponse = radarResponse;
        throw new Error(radarResponse.message || 'No trip ID found for this AWB.');
      }

      const tripId = radarResponse.data.tripId;
      setLoadingStage('session');

      // Step 2: Create call session using tripId
      const sessionResponse = await createCallSession(tripId);
      
      if (!sessionResponse) {
        errorClass = 'network';
        throw new Error('Failed to create call session. Please try again.');
      }

      if (!sessionResponse.data) {
        errorResponse = sessionResponse;
        throw new Error(sessionResponse.message || 'No phone number received from call session.');
      }

      // Set the phone number from the response
      setPhoneNumber(sessionResponse.data);
      track('tp_call_session', { step: 'created', via: 'deep_link', elapsed_ms: elapsed() });
      
    } catch (err) {
      console.error('Call session error:', err);
      track('tp_call_session', { step: 'failed', via: 'deep_link', error_class: errorClass, code: errorResponse ? errorCode(errorResponse) : undefined, elapsed_ms: elapsed() });
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetState = () => {
    setIsLoading(false);
    setError(null);
    setPhoneNumber(null);
    setLoadingStage('radar');
  };

  useEffect(() => {
    if (shouldCreateSession) {
      initiateCallSession();
    }
  }, [awb, shouldCreateSession]);

  return {
    isLoading,
    error,
    phoneNumber,
    loadingStage,
    retry: initiateCallSession,
    reset: resetState
  };
};
